import { useEffect, useMemo, useState } from 'react';
import { ToneBatchChoiceStep } from './toneSession/components/ToneBatchChoiceStep';
import { ToneBatchWriteStep } from './toneSession/components/ToneBatchWriteStep';
import { ToneAvoidStep } from './toneSession/components/ToneAvoidStep';
import { ToneReviewStep } from './toneSession/components/ToneReviewStep';
import { ToneDoneStep } from './toneSession/components/ToneDoneStep';
import { logError } from '@/lib/logger';
import Layout from '@/layouts/Layout';
import { Heading } from '@/components/ui/heading';
import { csrfFetch } from '@/lib/csrf';

type ScenarioOption = {
  user_message: string;
  options?: string[];
};

type ToneSessionPayload = {
  status?: string;
  batch1?: { scenarios?: ScenarioOption[]; responses?: string[] };
  batch2?: { scenarios?: ScenarioOption[]; choices?: string[] };
  avoid?: string;
  distilled?: { guidelines?: string[]; examples?: { context: string; reply: string }[] };
};

type ToneProfile = {
  guidelines?: string[];
  examples?: { context: string; reply: string }[];
  generated_at?: string;
};

type Step = 'intro' | 'batch1' | 'batch2' | 'avoid' | 'review' | 'done';

export default function ToneSession({
  toneSession,
  toneProfile,
}: {
  toneSession: ToneSessionPayload;
  toneProfile: ToneProfile;
}) {
  const [session, setSession] = useState<ToneSessionPayload>(toneSession || {});
  const [profile, setProfile] = useState<ToneProfile>(toneProfile || {});
  const [submitting, setSubmitting] = useState(false);
  const batch1Scenarios = session?.batch1?.scenarios || [];
  const batch1Responses = session?.batch1?.responses || [];
  const batch2Scenarios = session?.batch2?.scenarios || [];
  const batch2Choices = session?.batch2?.choices || [];

  const [responses, setResponses] = useState<string[]>(batch1Responses);
  const [choices, setChoices] = useState<string[]>(batch2Choices);
  const [avoid, setAvoid] = useState<string>(session?.avoid || '');
  const [writeIndex, setWriteIndex] = useState(0);
  const [choiceIndex, setChoiceIndex] = useState(0);

  useEffect(() => {
    // sync derived inputs when session changes (e.g., after advance)
    setResponses(session?.batch1?.responses || []);
    setChoices(session?.batch2?.choices || []);
    setAvoid(session?.avoid || '');
    setWriteIndex(0);
    setChoiceIndex(0);
  }, [session]);

  const currentStep: Step = useMemo(() => {
    const status = session?.status;
    if (!status) return 'intro';
    if (status === 'batch1') return 'batch1';
    if (status === 'batch2') return 'batch2';
    if (status === 'avoid') return 'avoid';
    if (status === 'review') return 'review';
    if (status === 'distilled') return 'done';
    return 'intro';
  }, [session?.status]);

  const progress = useMemo(() => {
    switch (currentStep) {
      case 'intro':
        return 0;
      case 'batch1':
        return 25;
      case 'batch2':
        return 50;
      case 'avoid':
        return 70;
      case 'review':
        return 90;
      case 'done':
        return 100;
      default:
        return 0;
    }
  }, [currentStep]);

  const advance = async (step: string, payload: Record<string, unknown> = {}) => {
    setSubmitting(true);
    try {
      const res = await csrfFetch('/therapists/tone_session/advance', {
        method: 'POST',
        body: JSON.stringify({ step, ...payload }),
      });

      if (!res.ok) {
        throw new Error(`advance_failed_${res.status}`);
      }

      const data = await res.json();
      if (data.tone_session) setSession(data.tone_session as ToneSessionPayload);
      if (data.tone_profile) setProfile(data.tone_profile as ToneProfile);
    } catch (err) {
      logError('Advance tone session failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const start = () => advance('start');
  const submitBatch1 = () => advance('batch1', { responses });
  const submitBatch2 = () => advance('batch2', { choices });
  const submitAvoid = () => advance('avoid', { avoid });
  const finalize = () => advance('finalize');
  const reset = () => advance('start');

  const handleSelectChoice = (idx: number, value: string) => {
    const next = [...choices];
    next[idx] = value;
    setChoices(next);
  };

  const handleChangeResponse = (idx: number, value: string) => {
    const next = [...responses];
    next[idx] = value;
    setResponses(next);
  };

  const goNextWrite = () => {
    if (writeIndex < batch1Scenarios.length - 1) {
      setWriteIndex(writeIndex + 1);
    } else {
      submitBatch1();
    }
  };

  const goPrevWrite = () => {
    setWriteIndex(Math.max(writeIndex - 1, 0));
  };

  const goNextChoice = () => {
    if (choiceIndex < batch2Scenarios.length - 1) {
      setChoiceIndex(choiceIndex + 1);
    } else {
      submitBatch2();
    }
  };

  const goPrevChoice = () => {
    setChoiceIndex(Math.max(choiceIndex - 1, 0));
  };

  return (
    <Layout title="Tone of voice" isAdmin>
      <div className="w-full space-y-4">
        {currentStep === 'intro' ? (
          <>
            <div className="space-y-3">
              <Heading level={1}>Costruiamo il tuo tono in 3 mosse</Heading>
              <p className="text-base text-muted-foreground">
                Scrivi su scenari tipo, scegli tra tre opzioni, e applichiamo il tuo stile alle
                chat.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3 text-sm text-muted-foreground">
              <div className="rounded-lg border border-border/70 bg-card/70 p-3">
                <div className="text-xs font-semibold text-primary mb-1">Passo 1</div>
                <div className="text-foreground font-medium">Scrivi 3 risposte</div>
                <p>Scenari fittizi: rispondi con il tuo modo di parlare.</p>
              </div>
              <div className="rounded-lg border border-border/70 bg-card/70 p-3">
                <div className="text-xs font-semibold text-primary mb-1">Passo 2</div>
                <div className="text-foreground font-medium">Scegli il tuo suono</div>
                <p>3 opzioni generate sul tuo stile, ne scegli una per scenario.</p>
              </div>
              <div className="rounded-lg border border-border/70 bg-card/70 p-3">
                <div className="text-xs font-semibold text-primary mb-1">Passo 3</div>
                <div className="text-foreground font-medium">Applichiamo il tono</div>
                <p>Il profilo viene usato in tutte le chat dei tuoi pazienti.</p>
              </div>
            </div>

            <div className="rounded-lg border border-border/70 bg-card/70 p-3 text-sm text-muted-foreground">
              <div className="font-medium text-foreground mb-1">Perché ti conviene</div>
              <p>
                Risposte coerenti con te, meno tempo a riscrivere prompt, più fiducia dei pazienti.
              </p>
            </div>

            <div className="rounded-lg border border-border/70 bg-card/70 p-3 text-sm text-muted-foreground">
              <div className="font-medium text-foreground mb-1">Cosa ottieni</div>
              <ul className="list-disc ml-4 space-y-1">
                <li>Un profilo di tono riutilizzato in tutte le chat.</li>
                <li>Opzioni generate sul tuo stile, non su prompt generici.</li>
                <li>Coerenza percepita dai pazienti anche quando non rispondi in diretta.</li>
              </ul>
            </div>

            <div>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-3 text-primary-foreground text-sm font-semibold shadow-sm hover:bg-primary/90 focus-visible:outline-offset-2 focus-visible:outline-primary"
                disabled={submitting}
                onClick={start}
              >
                Inizia ora
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <Heading level={2}>Addestra EmoviBot sul tuo stile</Heading>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
                aria-label="Progresso intervista"
              />
            </div>
          </div>
        )}

        {currentStep === 'batch1' && batch1Scenarios[writeIndex] && (
          <ToneBatchWriteStep
            scenario={batch1Scenarios[writeIndex]}
            scenarioIndex={writeIndex}
            total={batch1Scenarios.length}
            response={responses[writeIndex]}
            submitting={submitting}
            onChangeResponse={(val) => handleChangeResponse(writeIndex, val)}
            onNext={goNextWrite}
            onPrev={goPrevWrite}
            isLast={writeIndex === batch1Scenarios.length - 1}
          />
        )}

        {currentStep === 'batch2' && batch2Scenarios[choiceIndex] && (
          <ToneBatchChoiceStep
            scenario={batch2Scenarios[choiceIndex]}
            scenarioIndex={choiceIndex}
            total={batch2Scenarios.length}
            choice={choices[choiceIndex]}
            submitting={submitting}
            onSelect={(val) => handleSelectChoice(choiceIndex, val)}
            onNext={goNextChoice}
            onPrev={goPrevChoice}
            isLast={choiceIndex === batch2Scenarios.length - 1}
          />
        )}

        {currentStep === 'avoid' && (
          <ToneAvoidStep
            value={avoid}
            submitting={submitting}
            onChange={setAvoid}
            onNext={submitAvoid}
            onCancel={reset}
          />
        )}

        {currentStep === 'review' && (
          <ToneReviewStep
            batch1Scenarios={batch1Scenarios}
            batch1Responses={batch1Responses.length ? batch1Responses : responses}
            batch2Scenarios={batch2Scenarios}
            batch2Choices={batch2Choices.length ? batch2Choices : choices}
            avoid={avoid}
            submitting={submitting}
            onFinalize={finalize}
            onReset={reset}
          />
        )}

        {currentStep === 'done' && (
          <ToneDoneStep profile={profile} submitting={submitting} onReset={reset} />
        )}
      </div>
    </Layout>
  );
}
